import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import PermissionGuard from "../PermissionGuard";
import { useAuth } from "@/contexts/AuthContext";
import { PuffinLoader } from "../ui/PuffinLoader";
import {
  recoverFromAuthError,
  getAuthErrorMessage,
} from "../../utils/authRecovery";
import { forceLogout, checkAuthState } from "../../utils/forceLogout";
import {
  ShoppingCart,
  Clock,
  CheckCircle,
  XCircle,
  Banknote,
  TrendingUp,
  FileText,
  Users,
  AlertTriangle,
  Calendar,
  Eye,
  Download,
  User,
  Building,
  LayoutGrid,
  List as ListIcon,
  BarChart3,
  X,
  ChevronLeft,
  ChevronRight,
  Shield,
  RefreshCw,
  UserCheck,
  Package,
  DollarSign,
  CreditCard,
  Truck,
  Trash2
} from 'lucide-react';
import PurchaseReceipt from './PurchaseReceipt';
import { AllPurchaseCharts } from './PurchaseRequestCharts';
import purchaseRequestService from '@/services/purchaseRequestService';

const PurchaseRequestDashboard = () => {
  const { hasPermission, isAuthenticated, user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("kanban"); // 'kanban', 'list', 'stats'
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [modalTitle, setModalTitle] = useState("");
  const [showReceipt, setShowReceipt] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [collapsedColumns, setCollapsedColumns] = useState({});
  const [showPaymentAuthModal, setShowPaymentAuthModal] = useState(false);
  const [authorizingRequest, setAuthorizingRequest] = useState(null);
  const [authComments, setAuthComments] = useState("");

  const toggleColumn = (stageKey) => {
    setCollapsedColumns((prev) => ({
      ...prev,
      [stageKey]: !prev[stageKey],
    }));
  };

  useEffect(() => {
    // Always try to fetch data - let the API handle authentication
    fetchDashboardData();
  }, []);

  const handleAuthRecovery = async () => {
    try {
      setError(null);
      setLoading(true);

      const result = await recoverFromAuthError();
      const success = result === "recovered";
      if (success) {
        // Retry loading data
        await fetchDashboardData();
      } else {
        setError("Recovery failed. Please try logging out and back in.");
      }
    } catch (error) {
      console.error("Recovery failed:", error);
      setError("Recovery failed. Please try logging out and back in.");
    } finally {
      setLoading(false);
    }
  };

  const handleForceLogout = () => {
    console.log("🔄 Force logout initiated from dashboard");
    checkAuthState(); // Log current state
    forceLogout();
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get tokens from both sources for comparison
      const localStorageToken = localStorage.getItem("token");
      const authContextToken = token;

      console.log("🔍 Token Debug Info:", {
        localStorageToken: localStorageToken
          ? `${localStorageToken.substring(0, 20)}...`
          : "Missing",
        authContextToken: authContextToken
          ? `${authContextToken.substring(0, 20)}...`
          : "Missing",
        tokensMatch: localStorageToken === authContextToken,
        isAuthenticated: isAuthenticated,
        userEmail: user?.email,
      });

      // Use AuthContext token first, fallback to localStorage
      const tokenToUse = authContextToken || localStorageToken;

      if (!tokenToUse) {
        setError("Please log in to view the dashboard.");
        setLoading(false);
        return;
      }

      console.log(
        "🔑 Using token from:",
        authContextToken ? "AuthContext" : "localStorage",
      );

      // Get API base URL from environment
      const apiBaseUrl =
        import.meta.env.VITE_API_URL ||
        "https://srv1309580.hstgr.cloud/puffin/api/v1";

      // Test token with a simple endpoint first
      console.log("🧪 Testing token with /auth/me endpoint...");
      try {
        const testResponse = await fetch(`${apiBaseUrl}/auth/me`, {
          headers: {
            Authorization: `Bearer ${tokenToUse}`,
            "Content-Type": "application/json",
          },
        });
        console.log("🧪 Token test result:", {
          status: testResponse.status,
          ok: testResponse.ok,
        });

        if (!testResponse.ok) {
          const testError = await testResponse.text();
          console.log("🧪 Token test error:", testError);
        }
      } catch (testError) {
        console.log("🧪 Token test failed:", testError.message);
      }

      // Fetch both stats and requests in parallel
      const [statsResponse, requestsResponse] = await Promise.all([
        fetch(`${apiBaseUrl}/purchase-requests/dashboard/stats`, {
          headers: {
            Authorization: `Bearer ${tokenToUse}`,
            "Content-Type": "application/json",
          },
        }),
        fetch(`${apiBaseUrl}/purchase-requests/dashboard/requests?limit=100`, {
          headers: {
            Authorization: `Bearer ${tokenToUse}`,
            "Content-Type": "application/json",
          },
        }),
      ]);

      // Check for authentication errors
      if (statsResponse.status === 401 || requestsResponse.status === 401) {
        throw new Error("Authentication failed. Please log in again.");
      }

      if (statsResponse.status === 403 || requestsResponse.status === 403) {
        // Get error details to understand the issue
        let errorText = "";
        try {
          errorText = await (statsResponse.ok
            ? requestsResponse.text()
            : statsResponse.text());
          console.log("403 Error details:", errorText);
        } catch (e) {
          console.log("Could not parse error response");
        }

        // Only treat as token issue if explicitly mentioned
        if (
          errorText.includes("Invalid token") ||
          errorText.includes("jwt malformed") ||
          errorText.includes("invalid signature") ||
          errorText.includes("JsonWebTokenError")
        ) {
          throw new Error(
            "Your login session is no longer valid. This usually happens after a server restart. Please log out and log back in.",
          );
        }

        // Otherwise it's likely a permissions issue
        throw new Error(
          "Access denied. You may not have permission to view the Purchase Dashboard.",
        );
      }

      if (!statsResponse.ok || !requestsResponse.ok) {
        let errorText = "";
        try {
          errorText = await (statsResponse.ok
            ? requestsResponse.text()
            : statsResponse.text());
          console.error("API Error Response:", {
            statsStatus: statsResponse.status,
            requestsStatus: requestsResponse.status,
            errorText: errorText,
          });
        } catch (e) {
          console.error("Could not parse error response");
        }

        // Provide more specific error messages
        if (statsResponse.status === 404 || requestsResponse.status === 404) {
          throw new Error(
            "Dashboard endpoints not found. Please contact your administrator.",
          );
        }

        throw new Error(
          `Failed to fetch dashboard data (${statsResponse.status}/${requestsResponse.status}). Please try again later.`,
        );
      }

      const [statsData, requestsData] = await Promise.all([
        statsResponse.json(),
        requestsResponse.json(),
      ]);

      // Debug logging to see what we're getting
      console.log("Dashboard API responses:", {
        statsData: statsData,
        requestsData: requestsData,
        statsSuccess: statsData.success,
        requestsSuccess: requestsData.success,
      });

      setStats(statsData.data);
      
      // Map requests and extract profile_pic from nested Requester object
      const mappedRequests = (requestsData.data.requests || []).map(request => {
        console.log('Request mapping:', {
          request_id: request.request_id,
          requester_name: request.requester_name,
          requester_id: request.requester_id,
          Requester: request.Requester,
          cdn_avatar_url: request.Requester?.cdn_avatar_url,
          avatar: request.Requester?.avatar
        });
        
        return {
          ...request,
          requester_profile_pic: request.Requester?.cdn_avatar_url || request.Requester?.avatar || null
        };
      });
      
      console.log('Mapped requests sample:', mappedRequests[0]);
      setRequests(mappedRequests);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setError(error.message);

      // Show toast for non-auth errors
      if (
        !error.message.includes("Authentication") &&
        !error.message.includes("session has expired")
      ) {
        toast.error("Failed to load dashboard data. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      submitted: "bg-blue-100 text-blue-800",
      pending_approval: "bg-yellow-100 text-yellow-800",
      pending_procurement_review: "bg-purple-100 text-purple-800",
      pending_finance_approval: "bg-orange-100 text-orange-800",
      payment_in_progress: "bg-indigo-100 text-indigo-800",
      awaiting_delivery_confirmation: "bg-teal-100 text-teal-800",
      completed: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getStageColor = (stage) => {
    const colors = {
      request_creation: "bg-blue-100 text-blue-800",
      approval_stage: "bg-yellow-100 text-yellow-800",
      procurement_stage: "bg-purple-100 text-purple-800",
      finance_stage: "bg-orange-100 text-orange-800",
      pay_vendor_stage: "bg-indigo-100 text-indigo-800",
      delivery_stage: "bg-teal-100 text-teal-800",
      completed: "bg-green-100 text-green-800",
    };
    return colors[stage] || "bg-gray-100 text-gray-800";
  };

  const formatCurrency = (amount) => {
    return `₦${parseFloat(amount).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const handleDelete = async (requestId, requesterId) => {
    // TEMPORARY: Log user data for debugging
    console.log('🔍 User data in handleDelete:', {
      user,
      userId: user?.id,
      userTypeId: user?.user_type_id,
      requesterId
    });

    if (!window.confirm('Are you sure you want to delete this request? This action cannot be undone.')) {
      return;
    }

    try {
      await purchaseRequestService.deleteRequest(requestId);
      toast.success('Request deleted successfully');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete request');
    }
  };

  const canDelete = (request) => {
    const isSystemAdmin = user?.user_type_id === 2;
    const isCreator = user?.id === request.requester_id;
    
    // Debug logging
    console.log('🔍 Delete Permission Check:', {
      requestId: request.request_id,
      userId: user?.id,
      userTypeId: user?.user_type_id,
      requesterId: request.requester_id,
      isSystemAdmin,
      isCreator,
      canDelete: isSystemAdmin || isCreator
    });
    
    return (isSystemAdmin || isCreator);
  };

  const handleDownloadReceipt = (request) => {
    setSelectedRequest(request);
    setShowReceipt(true);
  };

  const handlePaymentAuthorization = async (action) => {
    if (!authorizingRequest) return;

    try {
      setLoading(true);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || "https://srv1309580.hstgr.cloud/puffin/api/v1"}/purchase-requests/${authorizingRequest.request_id}/authorize-payment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token || localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            action, // 'approve' or 'reject'
            comments: authComments,
            authorized_by: user?.id,
            authorized_at: new Date().toISOString(),
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to process payment authorization");
      }

      const data = await response.json();

      toast.success(
        action === "approve"
          ? "Payment authorized successfully! Request moved to Pay Vendor stage."
          : "Payment authorization rejected.",
      );

      // Refresh dashboard data
      await fetchDashboardData();

      // Close modal and reset
      setShowPaymentAuthModal(false);
      setAuthorizingRequest(null);
      setAuthComments("");
    } catch (error) {
      console.error("Payment authorization error:", error);
      toast.error(error.message || "Failed to process payment authorization");
    } finally {
      setLoading(false);
    }
  };

  // Group requests by stage for Kanban board
  const groupRequestsByStage = () => {
    const stages = {
      approval_stage: {
        title: "Manager Approval",
        requests: [],
        color: "bg-blue-50",
        borderColor: "border-blue-200",
        headerColor: "bg-gradient-to-r from-blue-500 to-blue-600",
        icon: UserCheck,
      },
      procurement_stage: {
        title: "Procurement Confirmation",
        requests: [],
        color: "bg-purple-50",
        borderColor: "border-purple-200",
        headerColor: "bg-gradient-to-r from-purple-500 to-purple-600",
        icon: Package,
      },
      finance_stage: {
        title: "Finance Confirmation",
        requests: [],
        color: "bg-indigo-50",
        borderColor: "border-indigo-200",
        headerColor: "bg-gradient-to-r from-indigo-500 to-indigo-600",
        icon: DollarSign,
      },
      md_approval_stage: {
        title: "GMD Approval",
        requests: [],
        color: "bg-orange-50",
        borderColor: "border-orange-200",
        headerColor: "bg-gradient-to-r from-orange-500 to-orange-600",
        icon: Shield,
      },
      payment_authorization_stage: {
        title: "Payment Authorization",
        requests: [],
        color: "bg-pink-50",
        borderColor: "border-pink-200",
        headerColor: "bg-gradient-to-r from-pink-500 to-pink-600",
        icon: CreditCard,
      },
      pay_vendor_stage: {
        title: "Pay Vendor",
        requests: [],
        color: "bg-pink-50",
        borderColor: "border-pink-200",
        headerColor: "bg-gradient-to-r from-pink-500 to-pink-600",
        icon: Truck,
      },
      confirm_payment_stage: {
        title: "Awaiting Bank Payment",
        requests: [],
        color: "bg-teal-50",
        borderColor: "border-teal-200",
        headerColor: "bg-gradient-to-r from-teal-500 to-teal-600",
        icon: Banknote,
      },
      delivery_stage: {
        title: "Request Closed",
        requests: [],
        color: "bg-green-50",
        borderColor: "border-green-200",
        headerColor: "bg-gradient-to-r from-green-500 to-green-600",
        icon: CheckCircle,
      },
      rejected: {
        title: "Rejected",
        requests: [],
        color: "bg-red-50",
        borderColor: "border-red-200",
        headerColor: "bg-gradient-to-r from-red-500 to-red-600",
        icon: XCircle,
      },
    };

    requests.forEach(request => {
      const stage = request.current_stage;
      const status = request.status;
      
      if (status === 'rejected') {
        stages.rejected.requests.push(request);
      } else if (stage && stages[stage]) {
        stages[stage].requests.push(request);
      } else {
        console.warn('Request with unmapped stage:', { 
          id: request.request_id, 
          stage: stage, 
          status: status 
        });
      }
    });

    return stages;
  };

  const kanbanStages = groupRequestsByStage();

  // Render Kanban View
  const renderKanbanView = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">
        Purchase Request Workflow
      </h3>
      <div className="overflow-x-auto">
        <div className="flex space-x-4 min-w-max pb-4">
          {Object.entries(kanbanStages).map(([stageKey, stage]) => {
            const isCollapsed = collapsedColumns[stageKey];
            const Icon = stage.icon;
            
            return (
              <div 
                key={stageKey} 
                className={`flex-shrink-0 transition-all duration-300 ${isCollapsed ? 'w-16' : 'w-80'} ${stage.color} border-2 ${stage.borderColor} rounded-xl shadow-sm overflow-hidden`}
              >
                {isCollapsed ? (
                  <div className="h-full flex flex-col items-center justify-between py-4">
                    <button
                      onClick={() => toggleColumn(stageKey)}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors mb-2"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                    <div className="flex-1 flex items-center justify-center">
                      <div className="transform -rotate-90 whitespace-nowrap">
                        <h4 className="font-semibold text-gray-900 text-sm">
                          {stage.title}
                        </h4>
                      </div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-bold mt-2">
                      {stage.requests.length}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={`${stage.headerColor} text-white px-4 py-3`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className="h-5 w-5" />
                          <h4 className="font-bold text-sm">{stage.title}</h4>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-bold">
                            {stage.requests.length}
                          </span>
                          <button
                            onClick={() => toggleColumn(stageKey)}
                            className="p-1 hover:bg-white/20 rounded transition-colors"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 space-y-3 max-h-[calc(100vh-320px)] overflow-y-auto custom-scrollbar">
                      {stage.requests.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                          <Icon className="h-12 w-12 mx-auto mb-3 opacity-30" />
                          <p className="text-sm font-medium">No requests</p>
                        </div>
                      ) : (
                        stage.requests.map((request) => (
                          <div key={request.id} className="group bg-white rounded-xl border border-gray-200 p-4 hover:shadow-xl hover:border-blue-300 transition-all duration-200 cursor-pointer">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-2">
                                {request.requester_profile_pic ? (
                                  <img 
                                    src={request.requester_profile_pic} 
                                    alt={request.requester_name}
                                    className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                                  />
                                ) : (
                                  <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                    {request.requester_name?.charAt(0).toUpperCase() || 'U'}
                                  </div>
                                )}
                                <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-md">
                                  {request.request_id}
                                </span>
                              </div>
                              <span className={`px-2 py-0.5 rounded-md text-xs font-semibold uppercase tracking-wide ${
                                request.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                                request.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                                request.priority === 'medium' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {request.priority}
                              </span>
                            </div>
                            
                            <div className="space-y-2 mb-3">
                              <p className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug">
                                {request.item_service_requested}
                              </p>
                              <p className="text-xs text-gray-600 font-medium">
                                {request.requester_name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {request.department}
                              </p>
                              
                              {request.status === 'rejected' && request.rejection_reason && (
                                <div className="p-2 bg-red-50 border-l-4 border-red-500 rounded">
                                  <p className="text-xs font-semibold text-red-900">
                                    Rejected by: {
                                      request.rejected_by ||
                                      request.WorkflowApprovals?.find(a => a.action === 'reject')?.approver_name ||
                                      request.rejected_by_name ||
                                      request.ApprovingManager?.name || 
                                      'Unknown'
                                    }
                                  </p>
                                  <p className="text-xs text-red-700 mt-1">{request.rejection_reason}</p>
                                </div>
                              )}
                            </div>

                            <div className="flex items-center justify-between pt-3 border-t border-gray-100 mb-3">
                              <div className="flex items-center gap-1">
                                <Banknote className="h-4 w-4 text-green-600" />
                                <span className="text-sm font-bold text-gray-900">
                                  {formatCurrency(request.amount)}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500">
                                {formatDate(request.created_at)}
                              </span>
                            </div>
                            
                            <div className="pb-3 border-b border-gray-100">
                              <p className="text-xs text-gray-600">
                                <span className="font-medium">Vendor:</span> {request.vendor_name || 'N/A'}
                              </p>
                            </div>

                            <div className="mt-3 flex gap-2">
                              <Link
                                to={`/purchase-requests/${request.request_id}`}
                                className="flex-1 text-center px-3 py-2 text-xs font-medium bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                              >
                                View Details
                              </Link>
                              <button
                                onClick={() => handleDownloadReceipt(request)}
                                className="flex items-center justify-center px-3 py-2 text-xs font-medium bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                                title="Download Receipt"
                              >
                                <Download className="h-3.5 w-3.5" />
                              </button>
                              {/* TEMPORARY: Always show delete button for debugging */}
                              <button
                                onClick={() => handleDelete(request.request_id, request.requester_id)}
                                className="flex items-center justify-center px-3 py-2 text-xs font-medium bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                                title="Delete Request"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Render List View
  const renderListView = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">
        All Purchase Requests
      </h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Request ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Item/Service
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Requester
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Department
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Stage
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Priority
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {requests.map((request) => (
              <tr key={request.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <Link
                    to={`/purchase-requests/${request.request_id}`}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800"
                  >
                    {request.request_id}
                  </Link>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 max-w-xs truncate">
                    {request.item_service_requested}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {request.requester_name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {request.department}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-semibold text-gray-900">
                    {formatCurrency(request.amount)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(request.status)}`}
                  >
                    {request.status.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStageColor(request.current_stage)}`}
                  >
                    {request.current_stage.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      request.priority === "urgent"
                        ? "bg-red-100 text-red-800"
                        : request.priority === "high"
                          ? "bg-orange-100 text-orange-800"
                          : request.priority === "medium"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {request.priority}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center space-x-2">
                    <Link
                      to={`/purchase-requests/${request.request_id}`}
                      className="text-blue-600 hover:text-blue-900"
                      title="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </Link>
                    <button
                      onClick={() => handleDownloadReceipt(request)}
                      className="text-green-600 hover:text-green-900"
                      title="Download Receipt"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                    {/* TEMPORARY: Always show delete button for debugging */}
                    <button
                      onClick={() => handleDelete(request.request_id, request.requester_id)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete Request"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Render Stats View
  const renderStatsView = () => (
    <div className="space-y-6">
      {/* Status Distribution */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          Status Distribution
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(
            requests.reduce((acc, req) => {
              acc[req.status] = (acc[req.status] || 0) + 1;
              return acc;
            }, {}),
          ).map(([status, count]) => (
            <div key={status} className="bg-gray-50 rounded-lg p-4">
              <div
                className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold mb-2 ${getStatusColor(status)}`}
              >
                {status.replace(/_/g, " ")}
              </div>
              <div className="text-2xl font-bold text-gray-900">{count}</div>
              <div className="text-sm text-gray-600">
                {((count / requests.length) * 100).toFixed(1)}% of total
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Department Breakdown */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          Department Breakdown
        </h3>
        <div className="space-y-4">
          {Object.entries(
            requests.reduce((acc, req) => {
              const dept = req.department || "Unknown";
              if (!acc[dept]) {
                acc[dept] = { count: 0, amount: 0 };
              }
              acc[dept].count++;
              acc[dept].amount += parseFloat(req.amount);
              return acc;
            }, {}),
          )
            .sort((a, b) => b[1].count - a[1].count)
            .map(([dept, data]) => (
              <div
                key={dept}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{dept}</div>
                  <div className="text-sm text-gray-600">
                    {data.count} requests
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-gray-900">
                    {formatCurrency(data.amount)}
                  </div>
                  <div className="text-sm text-gray-600">
                    Avg: {formatCurrency(data.amount / data.count)}
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Priority Distribution */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          Priority Distribution
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {["urgent", "high", "medium", "low"].map((priority) => {
            const count = requests.filter(
              (req) => req.priority === priority,
            ).length;
            const amount = requests
              .filter((req) => req.priority === priority)
              .reduce((sum, req) => sum + parseFloat(req.amount), 0);

            return (
              <div key={priority} className="bg-gray-50 rounded-lg p-4">
                <div
                  className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold mb-2 ${
                    priority === "urgent"
                      ? "bg-red-100 text-red-800"
                      : priority === "high"
                        ? "bg-orange-100 text-orange-800"
                        : priority === "medium"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {priority.toUpperCase()}
                </div>
                <div className="text-2xl font-bold text-gray-900">{count}</div>
                <div className="text-sm text-gray-600">
                  {formatCurrency(amount)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <PuffinLoader size="md" message="Loading dashboard..." />
      </div>
    );
  }

  // Check if user has permission to view dashboard
  if (
    hasPermission &&
    typeof hasPermission === "function" &&
    !hasPermission("purchase-dashboard")
  ) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start">
            <AlertTriangle className="h-6 w-6 text-yellow-400 mr-3 mt-1" />
            <div className="flex-1">
              <h3 className="text-lg font-medium text-yellow-800 mb-2">
                Access Restricted
              </h3>
              <p className="text-yellow-700 mb-4">
                You don't have permission to view the Purchase Request
                Dashboard. Please contact your administrator to request access.
              </p>
              <Link
                to="/purchase-requests"
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
              >
                Go to Purchase Requests
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start">
            <XCircle className="h-6 w-6 text-red-400 mr-3 mt-1" />
            <div className="flex-1">
              <h3 className="text-lg font-medium text-red-800 mb-2">
                Dashboard Error
              </h3>
              <p className="text-red-700 mb-4">{error}</p>
              <div className="flex space-x-3">
                <button
                  onClick={fetchDashboardData}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Retry
                </button>
                {error.includes("Authentication") ||
                error.includes("token") ||
                error.includes("session") ||
                error.includes("login session") ? (
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => window.location.reload()}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                    >
                      Refresh Page
                    </button>
                    <Link
                      to="/login"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                    >
                      Go to Login
                    </Link>
                  </div>
                ) : error.includes("Please log in") ? (
                  <Link
                    to="/login"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Go to Login
                  </Link>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enhanced Header with Gradient Background - matching GMD Dashboard */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#16314C] via-[#1a3a5c] to-[#0d1f30] shadow-xl border border-[#DF383F]/20">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSIjZmZmIiBzdHJva2Utb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-40"></div>
        
        <div className="relative px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Purchase Requests Dashboard
              </h1>
              <p className="text-[#C8D6E8]">
                Monitor and manage purchase request workflows
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {/* View Mode Toggle */}
              <div className="flex bg-white/10 backdrop-blur-sm rounded-lg p-1">
                <button
                  onClick={() => setViewMode("kanban")}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
                    viewMode === "kanban"
                      ? "bg-[#DF6B38] text-white shadow-lg"
                      : "text-[#C8D6E8] hover:text-white hover:bg-white/10"
                  }`}
                >
                  <LayoutGrid size={18} />
                  <span className="text-sm font-medium">Kanban</span>
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
                    viewMode === "list"
                      ? "bg-[#DF6B38] text-white shadow-lg"
                      : "text-[#C8D6E8] hover:text-white hover:bg-white/10"
                  }`}
                >
                  <ListIcon size={18} />
                  <span className="text-sm font-medium">List</span>
                </button>
                <button
                  onClick={() => setViewMode("stats")}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
                    viewMode === "stats"
                      ? "bg-[#DF6B38] text-white shadow-lg"
                      : "text-[#C8D6E8] hover:text-white hover:bg-white/10"
                  }`}
                >
                  <BarChart3 size={18} />
                  <span className="text-sm font-medium">Statistics</span>
                </button>
              </div>

              <button
                onClick={fetchDashboardData}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-lg hover:bg-white/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                title="Refresh Dashboard Data"
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
                />
                <span className="text-sm font-medium">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="px-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Requests Card */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg hover:shadow-xl transition-shadow duration-300 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
              <ShoppingCart className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
            <TrendingUp className="h-4 w-4 text-white/60" strokeWidth={2} />
          </div>
          
          <div>
            <p className="text-xs font-semibold text-white/80 uppercase tracking-wide mb-1">
              Total Requests
            </p>
            <p className="text-3xl font-bold text-white mb-2">
              {stats?.overview?.total_requests || 0}
            </p>
            <p className="text-xs text-white/80">
              <span className="font-semibold">{stats?.overview?.this_month || 0}</span> this month
            </p>
          </div>
        </div>

        {/* Total Amount Card */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-500 to-green-600 shadow-lg hover:shadow-xl transition-shadow duration-300 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
              <Banknote className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
            <TrendingUp className="h-4 w-4 text-white/60" strokeWidth={2} />
          </div>
          
          <div>
            <p className="text-xs font-semibold text-white/80 uppercase tracking-wide mb-1">
              Total Amount Requested
            </p>
            <p className="text-2xl font-bold text-white break-words mb-2">
              ₦{(stats?.overview?.total_amount || 0).toLocaleString()}
            </p>
            <p className="text-xs text-white/80">
              All Requests
            </p>
          </div>
        </div>

        {/* Total Approved Card */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 shadow-lg hover:shadow-xl transition-shadow duration-300 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
              <Clock className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
            <Calendar className="h-4 w-4 text-white/60" strokeWidth={2} />
          </div>
          
          <div>
            <p className="text-xs font-semibold text-white/80 uppercase tracking-wide mb-1">
              Total Approved
            </p>
            <p className="text-3xl font-bold text-white mb-2">
              {stats?.overview?.approved_requests || 0}
            </p>
            <p className="text-xs text-white/80">
              Approved Requests
            </p>
            <p className="text-xs text-white/60 mt-0.5">
              Delivery: ₦{(stats?.overview?.delivery_stage_amount || 0).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Pending Amount Card */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg hover:shadow-xl transition-shadow duration-300 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
              <TrendingUp className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
            <BarChart3 className="h-4 w-4 text-white/60" strokeWidth={2} />
          </div>
          
          <div>
            <p className="text-xs font-semibold text-white/80 uppercase tracking-wide mb-1">
              Pending Request Amount
            </p>
            <p className="text-2xl font-bold text-white break-words mb-2">
              ₦{(stats?.overview?.pending_manager_amount || 0).toLocaleString()}
            </p>
            <p className="text-xs text-white/80">
              Manager Approval Stage
            </p>
          </div>
        </div>
      </div>

      {/* Payment Authorization Section - REMOVED (no longer part of workflow) */}

      {/* View Content - Kanban Board */}
      {viewMode === "kanban" && renderKanbanView()}
      {viewMode === "list" && renderListView()}
      {viewMode === "stats" && renderStatsView()}

      {/* Analytics Charts Section */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Purchase Analytics
            </h2>
            <p className="text-gray-600 mt-1">
              Visual insights into purchase request trends and patterns
            </p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-white border border-gray-200">
              📊 {requests.length} Requests
            </span>
          </div>
        </div>
        <AllPurchaseCharts requests={requests} />
      </div>

      {/* Quick Actions */}
      {/* <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <PermissionGuard permission="purchase-create" mode="item">
            <Link
              to="/purchase-requests/new"
              className="flex items-center justify-center space-x-2 bg-blue-50 hover:bg-blue-100 text-blue-700 px-4 py-3 rounded-lg transition-colors"
            >
              <ShoppingCart className="h-5 w-5" />
              <span className="font-medium">Create Request</span>
            </Link>
          </PermissionGuard>
          
          <PermissionGuard permission="purchase-pending" mode="item">
            <Link
              to="/purchase-requests/pending-approval"
              className="flex items-center justify-center space-x-2 bg-yellow-50 hover:bg-yellow-100 text-yellow-700 px-4 py-3 rounded-lg transition-colors"
            >
              <Clock className="h-5 w-5" />
              <span className="font-medium">Pending Approval</span>
            </Link>
          </PermissionGuard>
          
          <PermissionGuard permission="purchase-procurement" mode="item">
            <Link
              to="/purchase-requests/procurement"
              className="flex items-center justify-center space-x-2 bg-purple-50 hover:bg-purple-100 text-purple-700 px-4 py-3 rounded-lg transition-colors"
            >
              <Users className="h-5 w-5" />
              <span className="font-medium">Procurement</span>
            </Link>
          </PermissionGuard>
          
          <PermissionGuard permission="purchase-finance" mode="item">
            <Link
              to="/purchase-requests/finance"
              className="flex items-center justify-center space-x-2 bg-green-50 hover:bg-green-100 text-green-700 px-4 py-3 rounded-lg transition-colors"
            >
              <Banknote className="h-5 w-5" />
              <span className="font-medium">Finance</span>
            </Link>
          </PermissionGuard>
        </div>
      </div> */}

      {/* Administration Section */}
      {/* <PermissionGuard permission="purchase-settings" mode="item">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Administration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link
              to="/purchase-requests/settings"
              className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-md p-6 hover:shadow-lg transition-all border border-blue-200 hover:border-blue-300"
            >
              <div className="flex items-start">
                <div className="p-3 rounded-md bg-blue-100">
                  <span className="text-blue-600 text-xl">⚙️</span>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Workflow Settings</h3>
                  <p className="text-sm text-gray-600 mb-3">Configure approval thresholds and workflow stages</p>
                </div>
              </div>
            </Link>

            <Link
              to="/purchase-requests/settings"
              className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg shadow-md p-6 hover:shadow-lg transition-all border border-green-200 hover:border-green-300"
            >
              <div className="flex items-start">
                <div className="p-3 rounded-md bg-green-100">
                  <span className="text-green-600 text-xl">👥</span>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Manager Assignment</h3>
                  <p className="text-sm text-gray-600 mb-3">Assign department managers and approval hierarchies</p>
                </div>
              </div>
            </Link>

            <Link
              to="/purchase-requests/history"
              className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg shadow-md p-6 hover:shadow-lg transition-all border border-purple-200 hover:border-purple-300"
            >
              <div className="flex items-start">
                <div className="p-3 rounded-md bg-purple-100">
                  <span className="text-purple-600 text-xl">📊</span>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Request History</h3>
                  <p className="text-sm text-gray-600 mb-3">View complete history of all purchase requests</p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </PermissionGuard> */}
      {/* Purchase Receipt Modal */}
      {showReceipt && selectedRequest && (
        <PurchaseReceipt
          request={selectedRequest}
          onClose={() => {
            setShowReceipt(false);
            setSelectedRequest(null);
          }}
        />
      )}

      {/* MD APPROVAL MODAL */}
      {/* Payment Authorization Modal */}
      {showPaymentAuthModal && authorizingRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Shield className="h-8 w-8 mr-3" />
                  <div>
                    <h2 className="text-2xl font-bold">
                      Payment Authorization
                    </h2>
                    <p className="text-indigo-100 text-sm mt-1">
                      Final approval before payment processing
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowPaymentAuthModal(false);
                    setAuthorizingRequest(null);
                    setAuthComments("");
                  }}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Request Details */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-gray-600" />
                  Request Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Request ID</p>
                    <p className="font-semibold text-gray-900">
                      PR-{authorizingRequest.request_id}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Amount</p>
                    <p className="font-semibold text-green-600 text-lg">
                      {formatCurrency(authorizingRequest.amount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Requester</p>
                    <p className="font-semibold text-gray-900">
                      {authorizingRequest.requester_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Department</p>
                    <p className="font-semibold text-gray-900">
                      {authorizingRequest.department}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-gray-600">Description</p>
                    <p className="font-medium text-gray-900">
                      {authorizingRequest.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* Authorization Info */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <h4 className="font-semibold text-indigo-900 mb-2 flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  Authorization Checklist
                </h4>
                <ul className="space-y-2 text-sm text-indigo-800">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 mr-2 mt-0.5 text-green-600" />
                    <span>Finance approval completed</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 mr-2 mt-0.5 text-green-600" />
                    <span>Budget allocation verified</span>
                  </li>
                  <li className="flex items-start">
                    <AlertTriangle className="h-4 w-4 mr-2 mt-0.5 text-yellow-600" />
                    <span>Final payment authorization required</span>
                  </li>
                </ul>
              </div>

              {/* Comments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Authorization Comments
                </label>
                <textarea
                  value={authComments}
                  onChange={(e) => setAuthComments(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter any comments or notes for this authorization..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowPaymentAuthModal(false);
                    setAuthorizingRequest(null);
                    setAuthComments("");
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handlePaymentAuthorization("reject")}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors flex items-center"
                  disabled={loading}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </button>
                <button
                  onClick={() => handlePaymentAuthorization("approve")}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors flex items-center"
                  disabled={loading}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {loading ? "Processing..." : "Authorize Payment"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default PurchaseRequestDashboard;
